'use client'

import { useState } from 'react'
import { useSignIn } from '@clerk/nextjs'
import { Text } from '@/design-system'
import { desktopRedirectOrFallback } from '@/hooks/auth/desktopRedirect'

export function GoogleButton() {
  const { signIn, fetchStatus } = useSignIn()
  const [isLoading, setIsLoading] = useState(false)
  const isReady = fetchStatus === 'idle' && !!signIn

  async function handleClick() {
    if (!isReady) return
    setIsLoading(true)
    try {
      // If the page was opened with `?desktop=1&state=...&challenge=...`,
      // route the post-OAuth callback directly to the desktop-auth issue
      // endpoint so the snaptransfer:// deep-link fires.
      const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
      const callback = desktopRedirectOrFallback(params, '/send')
      await signIn.sso({
        strategy: 'oauth_google',
        redirectUrl: '/sso-callback',
        redirectCallbackUrl: callback
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading || !isReady}
      style={{
        width: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '10px 14px',
        border: 'var(--border-w) solid var(--ink)',
        background: 'var(--paper)',
        color: 'var(--ink)',
        boxShadow: 'var(--shadow-sm)',
        cursor: isLoading || !isReady ? 'not-allowed' : 'pointer',
        opacity: isLoading || !isReady ? 0.7 : 1
      }}>
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      <Text as="span" variant="h4" weight={700} style={{ fontSize: 14, letterSpacing: '-0.01em' }}>
        {isLoading ? 'Redirecting…' : 'Continue with Google'}
      </Text>
    </button>
  )
}
